import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Package,
  IndianRupee,
  TrendingUp,
  Eye,
  RefreshCw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShipmentCalendarViewPage = () => {
  usePageTitle("Shipment Calendar View");
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayDetails, setShowDayDetails] = useState(false);
  const [dayOrders, setDayOrders] = useState([]);

  // Generate year options
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 2018; y--) {
      years.push(y);
    }
    return years;
  }, []);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Convert DDMMYY to Date object
  const parseDDMMYY = (dateStr) => {
    if (!dateStr || dateStr.length !== 6) return null;
    const day = parseInt(dateStr.substring(0, 2));
    const month = parseInt(dateStr.substring(2, 4)) - 1;
    const year = parseInt(dateStr.substring(4, 6));
    const fullYear = year >= 0 && year <= 30 ? 2000 + year : 1900 + year;
    return new Date(fullYear, month, day);
  };

  // Fetch orders for the selected month
  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Format dates for API (DDMMYY)
      const firstDay = new Date(selectedYear, selectedMonth, 1);
      const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
      
      const formatDDMMYY = (d) => {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = String(d.getFullYear()).slice(-2);
        return `${day}${month}${year}`;
      };

      const fromDate = formatDDMMYY(firstDay);
      const toDate = formatDDMMYY(lastDay);

      // Use the server-side per-day aggregate — fetching raw rows capped at
      // 5000 and truncated busy months (7k+ rows), understating totals.
      const response = await fetch(
        `${BACKEND_URL}/api/shipment-costing/daily-summary?date_from=${fromDate}&date_to=${toDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error("Failed to fetch orders");
      
      const data = await response.json();
      setOrders(data.daily_data || []);
    } catch (error) {
      toast.error("Failed to load calendar data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [selectedYear, selectedMonth, token]);

  // Group daily aggregates by day of month
  const ordersByDay = useMemo(() => {
    const grouped = {};
    orders.forEach(d => {
      const date = parseDDMMYY(d.date);
      if (date && date.getMonth() === selectedMonth && date.getFullYear() === selectedYear) {
        const day = date.getDate();
        grouped[day] = {
          count: d.orders || 0,
          ddmmyy: d.date,
          totalValue: d.total_cost || 0,
          totalMedicine: d.medicine_cost || 0,
          totalShipping: d.shipping_cost || 0,
        };
      }
    });
    return grouped;
  }, [orders, selectedMonth, selectedYear]);

  // Calculate monthly summary
  const monthlySummary = useMemo(() => {
    let totalOrders = 0;
    let totalValue = 0;
    let totalMedicine = 0;
    let totalShipping = 0;
    let daysWithOrders = 0;

    Object.values(ordersByDay).forEach(day => {
      totalOrders += day.count;
      totalValue += day.totalValue;
      totalMedicine += day.totalMedicine;
      totalShipping += day.totalShipping;
      daysWithOrders++;
    });

    return { totalOrders, totalValue, totalMedicine, totalShipping, daysWithOrders };
  }, [ordersByDay]);

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
    const lastDayOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startingDay = firstDayOfMonth.getDay(); // 0 = Sunday

    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: null, empty: true });
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = ordersByDay[day];
      days.push({
        day,
        empty: false,
        hasOrders: !!dayData,
        orderCount: dayData?.count || 0,
        totalValue: dayData?.totalValue || 0,
        isToday: 
          day === new Date().getDate() && 
          selectedMonth === new Date().getMonth() && 
          selectedYear === new Date().getFullYear()
      });
    }

    return days;
  }, [selectedYear, selectedMonth, ordersByDay]);

  // Handle day click — fetch that day's orders on demand
  const handleDayClick = async (day) => {
    if (!day || !ordersByDay[day]) return;
    setSelectedDate(day);
    setDayOrders([]);
    setShowDayDetails(true);
    try {
      const d = ordersByDay[day].ddmmyy;
      const res = await fetch(
        `${BACKEND_URL}/api/shipment-costing/orders?skip_summary=true&page=1&page_size=2000&date_from=${d}&date_to=${d}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setDayOrders(data.orders || []);
    } catch {
      toast.error("Failed to load day details");
    }
  };

  // Navigate months
  const navigateMonth = (direction) => {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;
    
    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    } else if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="p-4 lg:p-6 space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Calendar className="w-7 h-7 text-blue-600" />
            Shipment Calendar View
          </h1>
          <p className="text-slate-500 mt-1">Visual overview of orders by date</p>
        </div>
        
        <Button variant="outline" onClick={fetchOrders} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Monthly Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-3">
            <div className="text-xs text-blue-600">Total Orders</div>
            <div className="text-xl font-bold text-blue-700">
              {monthlySummary.totalOrders.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-3">
            <div className="text-xs text-green-600">Days with Orders</div>
            <div className="text-xl font-bold text-green-700">
              {monthlySummary.daysWithOrders}
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-3">
            <div className="text-xs text-purple-600">Medicine Cost</div>
            <div className="text-lg font-bold text-purple-700">
              {formatCurrency(monthlySummary.totalMedicine)}
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-3">
            <div className="text-xs text-orange-600">Shipping Cost</div>
            <div className="text-lg font-bold text-orange-700">
              {formatCurrency(monthlySummary.totalShipping)}
            </div>
          </CardContent>
        </Card>
        <Card className="border-teal-200 bg-teal-50">
          <CardContent className="p-3">
            <div className="text-xs text-teal-600">Total Value</div>
            <div className="text-lg font-bold text-teal-700">
              {formatCurrency(monthlySummary.totalValue)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar Navigation */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthNames.map((m, i) => (
                    <SelectItem key={i} value={String(i)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setSelectedYear(new Date().getFullYear());
                setSelectedMonth(new Date().getMonth());
              }}
            >
              Today
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="grid grid-cols-7 gap-2">
              {Array(35).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : (
            <>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((dayData, idx) => (
                  <div
                    key={idx}
                    onClick={() => dayData.hasOrders && handleDayClick(dayData.day)}
                    className={`
                      min-h-20 p-2 rounded-lg border transition-all
                      ${dayData.empty ? 'bg-slate-50 border-slate-100' : ''}
                      ${dayData.isToday ? 'ring-2 ring-blue-500 border-blue-300' : 'border-slate-200'}
                      ${dayData.hasOrders ? 'bg-green-50 border-green-200 cursor-pointer hover:bg-green-100 hover:border-green-300' : 'bg-white'}
                      ${!dayData.hasOrders && !dayData.empty ? 'bg-slate-50' : ''}
                    `}
                  >
                    {!dayData.empty && (
                      <>
                        <div className={`text-sm font-medium ${dayData.isToday ? 'text-blue-600' : 'text-slate-700'}`}>
                          {dayData.day}
                        </div>
                        {dayData.hasOrders && (
                          <div className="mt-1 space-y-1">
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              {dayData.orderCount} orders
                            </Badge>
                            <div className="text-xs text-slate-500 truncate">
                              {formatCurrency(dayData.totalValue)}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Day Details Dialog */}
      <Dialog open={showDayDetails} onOpenChange={setShowDayDetails}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Orders for {selectedDate} {monthNames[selectedMonth]} {selectedYear}
              <Badge className="ml-2">{dayOrders.length} orders</Badge>
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Tracking</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead className="text-right">Med Cost</TableHead>
                  <TableHead className="text-right">Ship Cost</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dayOrders.slice(0, 50).map((order, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-slate-500">{idx + 1}</TableCell>
                    <TableCell className="font-mono text-xs">{order.tracking || '-'}</TableCell>
                    <TableCell className="font-medium">{order.client_code || '-'}</TableCell>
                    <TableCell className="text-sm max-w-40 truncate">{order.product || order.second_name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{order.service || '-'}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(order.medicine_cost || 0)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(order.shipping_cost || 0)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(order.total_cost || 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {dayOrders.length > 50 && (
              <div className="text-center py-2 text-sm text-slate-500">
                Showing first 50 of {dayOrders.length} orders
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShipmentCalendarViewPage;
